import { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Box } from "@mui/material";
import {
  AttributeBaseType,
  AttributeComponentType,
  AttributeComponents,
  AttributeStrategy,
  ConfigurationComponentType,
  ConfigurationComponents,
  ControlComponents,
  LayoutComponentProvider,
  LayoutControlType,
  OtherComponentType,
  OtherComponents,
} from "@plansoft-configuration-renderer/components";
import { useCsConfiguration } from "@plansoft-configuration-renderer/framework";

import { useConfigurationTreeContext } from "@/context/ConfigurationTreeContext";

import { CustomAttributeHeadline } from "./attributes/CustomAttributeHeadline";
import { CustomAttributeWrapper } from "./attributes/CustomAttributeWrapper";
import { CustomAttributeGroup } from "./components/CustomAttributeGroup";
import { CustomCadClickViewer3D } from "./components/CustomCadClickViewer3D";
import { CustomConfigurationTree } from "./components/CustomConfigurationTree";
import { CustomUserDefinedFunctionButton } from "./components/CustomUserDefinedFunctionButton";
import { CustomEdit } from "./controls/CustomEdit";
import { CustomRadios } from "./controls/CustomRadios";
import { CustomSelect } from "./controls/CustomSelect";
import { CustomSubstructureMapper } from "./controls/CustomSubstructureMapper";
import { CustomTableControl } from "./controls/CustomTable";
import { CustomThumbnailControl } from "./controls/CustomThumbnail";

// override control components for attributes
const controlComponents = {
  ...ControlComponents,
  [LayoutControlType.Edit]: CustomEdit,
  [LayoutControlType.Radios]: CustomRadios,
  [LayoutControlType.Select]: CustomSelect,
  [LayoutControlType.Table]: CustomTableControl,
  [LayoutControlType.Substructure]: CustomSubstructureMapper,
  [LayoutControlType.Thumbnail]: CustomThumbnailControl,
};

// override configuration level components
const configurationComponents = {
  ...ConfigurationComponents,
  [ConfigurationComponentType.AttributeGroup]: CustomAttributeGroup,
  [ConfigurationComponentType.ConfigurationTree]: CustomConfigurationTree,
};

// override attribute components
const attributeComponents = {
  ...AttributeComponents,
  [AttributeComponentType.AttributeHeadline]: CustomAttributeHeadline,
  [AttributeBaseType.SingleOption]: CustomAttributeWrapper,
  [AttributeBaseType.MultiOption]: CustomAttributeWrapper,
  [AttributeBaseType.Substructure]: CustomAttributeWrapper,
  [AttributeBaseType.Editable]: CustomAttributeWrapper,
};

// override other components
const otherComponents = {
  ...OtherComponents,
  [OtherComponentType.UserDefinedFunctionButton]:
    CustomUserDefinedFunctionButton,
  [OtherComponentType.CadClickViewer3D]: CustomCadClickViewer3D,
};

export function CustomRenderer(): JSX.Element {
  // see properties of LayoutComponentProvider for more information
  const { pinned, expanded } = useConfigurationTreeContext();

  const { i18n } = useTranslation();
  const { localeId: configurationLanguage } = useCsConfiguration();

  useEffect(() => {
    if (configurationLanguage && i18n.language !== configurationLanguage) {
      i18n.changeLanguage(configurationLanguage);
      console.debug("i18n language changed to: ", configurationLanguage);
    }
  }, [i18n, configurationLanguage]);

  return (
    <Box
      className="custom-renderer-container"
      sx={{
        marginTop: 10,
        "@media (min-width:1400px)": {
          marginTop: 4,
        },
        marginLeft: pinned && expanded ? "calc(232px + 8px)" : "8px",
        transition: "margin-left 0.3s ease-in-out",
      }}
    >
      <LayoutComponentProvider
        configurationComponents={configurationComponents}
        attributeComponents={attributeComponents}
        controlComponents={controlComponents}
        otherComponents={otherComponents}
      >
        {/* Attribute strategy renders the components using the layout information */}
        <AttributeStrategy />
      </LayoutComponentProvider>
    </Box>
  );
}
